import React, { useEffect } from 'react';
import { useForm, FormProvider, UseFormProps, DefaultValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form } from 'react-bootstrap';
import { useAnalytics } from 'use-analytics';
import { FieldValues } from 'react-hook-form';
import { SubmitHandler } from 'react-hook-form';

interface FormWrapperProps<T extends FieldValues> extends UseFormProps<T> {
  schema?: yup.ObjectSchema<any>;
  onSubmit?: SubmitHandler<T>;
  children: React.ReactNode;
}

export const BUTTERFLY_SENTINEL = 'undefined';

// Butterfly stores the 'undefined' sentinel for required-but-unknown fields; forms that render
// those fields scrub their defaults so inputs render empty and required rules fire.
export const scrubSentinels = <T,>(value: T): T => {
  if (value === BUTTERFLY_SENTINEL) return '' as T;
  if (Array.isArray(value)) return value.map(scrubSentinels) as T;
  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, scrubSentinels(v)])) as T;
  }
  return value;
};

export const FormWrapper = <T extends FieldValues>({ schema, defaultValues, children, onSubmit,  ...props }: FormWrapperProps<T>) => {
  const analytics = useAnalytics();
  const form = useForm<T>({
    defaultValues,
    resolver: schema ? yupResolver(schema) : undefined,
    ...props,
  });

  const { reset, formState } = form;
  const { errors } = formState;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      analytics.track('FormValidationError', {
        errors: Object.entries(errors).map(([field, error]) => ({
          field,
          message: error?.message,
        })),
      });
    }
  }, [errors]);

  useEffect(() => {
    if (defaultValues) reset(defaultValues as T | DefaultValues<T> | undefined);
  }, [reset, defaultValues]);

  return (
    <FormProvider {...form}>
      <Form onSubmit={onSubmit && form.handleSubmit(onSubmit)}>
        {children}
      </Form>
    </FormProvider>
  );
};

export default FormWrapper;